import React, { useState, useEffect } from 'react';
import { RightCreateReq, RightResp, useAdminApi } from '../../api/adminApi';
import {
    Box, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField,
    Dialog, DialogActions, DialogContent, DialogTitle,
    Snackbar, Alert, IconButton,
    AlertColor
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import ConfirmationDialog from '../../common/ConfirmationDialog';

export const RightsPage = () => {
    const {
        createRight,
        getAllRights,
        deleteByRoleName,
        updateRight
    } = useAdminApi();

    const [rights, setRights] = useState<RightResp[]>([]);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [currentRight, setCurrentRight] = useState<RightCreateReq>({ name: '', description: '' });
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: AlertColor;
    }>({ open: false, message: '', severity: 'success' });

    const loadRights = async () => {
        const res = await getAllRights();
        if (res.data?.data) setRights(res.data.data);
    };
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [rightToDelete, setRightToDelete] = useState<string | null>(null);

    useEffect(() => { loadRights(); }, []);

    const handleCreate = async () => {
        try {
            await createRight(currentRight);
            setSnackbar({ open: true, message: 'Yetki oluşturuldu', severity: 'success' });
            loadRights();
            setOpenCreateDialog(false);
        } catch (error) {
            setSnackbar({ open: true, message: 'Oluşturma başarısız', severity: 'error' });
        }
    };

    const handleUpdate = async () => {
        try {
            await updateRight(currentRight.name, currentRight);
            setSnackbar({ open: true, message: 'Yetki güncellendi', severity: 'success' });
            loadRights();
            setOpenEditDialog(false);
        } catch (error) {
            setSnackbar({ open: true, message: 'Güncelleme başarısız', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (!rightToDelete) return;

        try {
            await deleteByRoleName(rightToDelete);
            setSnackbar({ open: true, message: 'Yetki silindi', severity: 'success' });
            loadRights();
        } catch (error) {
            setSnackbar({ open: true, message: 'Silme başarısız', severity: 'error' });
        } finally {
            setConfirmDeleteOpen(false);
            setRightToDelete(null);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                    setCurrentRight({ name: '', description: '' });
                    setOpenCreateDialog(true);
                }}
                sx={{ mb: 3 }}
            >
                Yeni Yetki Ekle
            </Button>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Yetki Adı</TableCell>
                            <TableCell>Açıklama</TableCell>
                            <TableCell>İşlemler</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rights.map((right) => (
                            <TableRow key={right.name}>
                                <TableCell>{right.name}</TableCell>
                                <TableCell>{right.description}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => {
                                        setCurrentRight(right);
                                        setOpenEditDialog(true);
                                    }}>
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => {
                                            setRightToDelete(right.name);
                                            setConfirmDeleteOpen(true);
                                        }}
                                        color="error"
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Yeni Yetki Dialog */}
            <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)}>
                <DialogTitle>Yeni Yetki Ekle</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Yetki Adı"
                        fullWidth
                        value={currentRight.name}
                        onChange={(e) => setCurrentRight({ ...currentRight, name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Açıklama"
                        fullWidth
                        multiline
                        rows={4}
                        value={currentRight.description}
                        onChange={(e) => setCurrentRight({ ...currentRight, description: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreateDialog(false)}>İptal</Button>
                    <Button onClick={handleCreate} color="primary">Oluştur</Button>
                </DialogActions>
            </Dialog>

            {/* Düzenleme Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
                <DialogTitle>Yetki Düzenle</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Yetki Adı"
                        fullWidth
                        value={currentRight.name}
                        InputProps={{ readOnly: true }}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Açıklama"
                        fullWidth
                        multiline
                        rows={4}
                        value={currentRight.description}
                        onChange={(e) => setCurrentRight({ ...currentRight, description: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>İptal</Button>
                    <Button onClick={handleUpdate} color="primary">Güncelle</Button>
                </DialogActions>
            </Dialog>
            <ConfirmationDialog
                open={confirmDeleteOpen}
                title="Yetki Silme Onayı"
                message={`"${rightToDelete}" yetkisini silmek istediğinize emin misiniz?`}
                onConfirm={handleDelete}
                onClose={() => setConfirmDeleteOpen(false)}
            />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};